import React, { useState, useEffect } from "react";
import { Form, Button, Dropdown, Input, Table, Message, Icon, Checkbox, Popup } from "semantic-ui-react";
import { map, size } from "lodash";
import moment from "moment";
import { toast } from "react-toastify";
import { HeaderPage } from "../../components/Admin";
import { ModalConfirm } from "../../components/Common";
import { useAuth, useSiteConfig } from "../../hooks";
import {
  getTipConfigApi,
  updateTipConfigApi,
  getTipSharesApi,
  markTipSharePaidApi,
} from "../../api/tips";
import { POSITIONS, TIP_MODELS, CARD_PAYOUTS, POINTS_HINT, positionText } from "../../utils/tips";

const money = (value) => `S/ ${Number(value || 0).toFixed(2)}`;

// Configuración → Propinas (Admin): la regla de reparto de este restaurante y
// las propinas de tarjeta por pagar (transferencia o planilla). El reparto en
// sí se hace en Caja → Apertura y cierre. Cada liquidación guarda una foto de
// la regla: cambiarla aquí no altera lo ya liquidado.
export function TipsAdmin() {
  const { auth } = useAuth();
  const token = auth?.token;
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [payNote, setPayNote] = useState("");
  // Interruptor de la instalación (SiteConfig.tips_enabled): Admin y Súper Usuario.
  const { siteConfig, getSiteConfig, updateSiteConfig } = useSiteConfig();
  const enabled = !!siteConfig?.tips_enabled;
  const onToggle = async (checked) => {
    try {
      await updateSiteConfig({ tips_enabled: checked });
      toast.success(checked ? "Propinas activadas" : "Propinas desactivadas");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const loadPending = async () => setPending(await getTipSharesApi(true, token));

  useEffect(() => {
    (async () => {
      try {
        getSiteConfig();
        setForm(await getTipConfigApi(token));
        await loadPending();
      } catch (error) {
        toast.error(error.message);
      }
    })();
  }, []);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const setPoints = (position, value) =>
    setForm((prev) => ({ ...prev, points: { ...prev.points, [position]: value } }));
  const setPercent = (position, value) =>
    setForm((prev) => ({ ...prev, percents: { ...prev.percents, [position]: value } }));
  const percentTotal = POSITIONS.reduce(
    (sum, p) => sum + Number(form?.percents?.[p.value] || 0),
    0
  );
  const percentOk = Math.abs(percentTotal - 100) < 0.001;

  const onSave = async () => {
    setSaving(true);
    try {
      setForm(
        await updateTipConfigApi(
          {
            model: form.model,
            card_fee_percent: form.card_fee_percent || 0,
            retention_percent: form.retention_percent || 0,
            card_payout: form.card_payout,
            points: Object.fromEntries(
              POSITIONS.map((p) => [p.value, form.points?.[p.value] ?? "1"])
            ),
            percents: Object.fromEntries(
              POSITIONS.map((p) => [p.value, form.percents?.[p.value] || "0"])
            ),
          },
          token
        )
      );
      toast.success("Regla de propinas guardada");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const onMarkPaid = async () => {
    try {
      await markTipSharePaidApi(payTarget.id, payNote.trim(), token);
      setPayTarget(null);
      setPayNote("");
      await loadPending();
      toast.success("Marcada como pagada");
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (!form) return null;

  return (
    <div>
      <HeaderPage title="Propinas" />
      <div style={{ margin: "30px 0 22px" }}>
        <Checkbox
          toggle
          label="Registrar propinas"
          checked={enabled}
          disabled={!siteConfig}
          onChange={(_, d) => onToggle(d.checked)}
        />
        <p style={{ color: "#777", marginTop: 12 }}>
          Activadas, al cobrar se anota la propina en efectivo y en tarjeta (no suma a la venta
          ni al IGV) y la Caja las reparte.
        </p>
      </div>
      {siteConfig && !enabled && (
        <Message warning>
          Las propinas están desactivadas: el cobro no las pide. Puedes dejar lista la regla de
          reparto igual.
        </Message>
      )}
      <Message info>
        La propina se declara en cada cuenta y se reparte en Caja → Apertura y cierre →
        «Propinas», antes de cerrar la caja. Lo que pongas aquí vale para las próximas
        liquidaciones; las ya hechas conservan su regla.
      </Message>

      <Form style={{ maxWidth: 560 }}>
        <Form.Field>
          <label>
            Cómo se reparte{" "}
            {/* ⓘ con Popup (el title nativo no se ve en el teléfono). */}
            <Popup
              trigger={
                <Icon name="info circle" link color="grey" aria-label="Qué modelo conviene" />
              }
              content={`Puntos por puesto: ${POINTS_HINT}.`}
              on={["hover", "click"]}
              position="right center"
              size="small"
            />
          </label>
          <Dropdown
            selection
            fluid
            options={TIP_MODELS}
            value={form.model}
            onChange={(_, d) => setField("model", d.value)}
          />
        </Form.Field>

        {form.model === "POINTS" && (
          <Form.Field>
            <label>Puntos por puesto (0 = no participa)</label>
            <p style={{ color: "#777", margin: "0 0 6px" }}>
              Son el peso de cada persona, no tienen que sumar nada: a cada uno le toca la
              bolsa × sus puntos ÷ la suma de los puntos de quienes participan. Con todos en 1
              es partes iguales. Lo que recibe un puesto en total depende de cuántas personas
              de ese puesto participen ese día.
            </p>
            <Table compact unstackable>
              <Table.Body>
                {map(POSITIONS, (p) => (
                  <Table.Row key={p.value}>
                    <Table.Cell>{p.text}</Table.Cell>
                    <Table.Cell>
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        value={form.points?.[p.value] ?? "1"}
                        onChange={(_, d) => setPoints(p.value, d.value)}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            {pointsExample(form.points) && (
              <p style={{ color: "#777" }}>
                Ejemplo con una persona por puesto, de cada S/ 100: {pointsExample(form.points)}.
              </p>
            )}
          </Form.Field>
        )}

        {form.model === "PERCENT" && (
          <Form.Field>
            <label>Porcentaje por puesto (deben sumar 100 %)</label>
            <p style={{ color: "#777", margin: "0 0 6px" }}>
              Cada puesto recibe su % fijo del total y lo reparte en partes iguales entre quienes
              de ese puesto participen ese día. Si de un puesto no participa nadie, su % se
              reparte entre los demás en proporción.
            </p>
            <Table compact unstackable>
              <Table.Body>
                {map(POSITIONS, (p) => (
                  <Table.Row key={p.value}>
                    <Table.Cell>{p.text}</Table.Cell>
                    <Table.Cell>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        label={{ basic: true, content: "%" }}
                        labelPosition="right"
                        value={form.percents?.[p.value] ?? "0"}
                        onChange={(_, d) => setPercent(p.value, d.value)}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
                <Table.Row>
                  <Table.Cell>
                    <strong>Suma</strong>
                  </Table.Cell>
                  <Table.Cell style={{ color: percentOk ? "#21ba45" : "#c0392b", fontWeight: 700 }}>
                    {Number(percentTotal.toFixed(2))} %{percentOk ? " ✓" : " — debe ser 100 %"}
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
            <p style={{ color: "#777" }}>
              Ejemplo: de S/ 100, los meseros reciben en total S/{" "}
              {Number(form.percents?.MESERO || 0).toFixed(2)}; si son 4, S/{" "}
              {(Number(form.percents?.MESERO || 0) / 4).toFixed(2)} cada uno.
            </p>
          </Form.Field>
        )}

        <Form.Field>
          <label>Propina de tarjeta: cómo se entrega</label>
          <Dropdown
            selection
            fluid
            options={CARD_PAYOUTS}
            value={form.card_payout}
            onChange={(_, d) => setField("card_payout", d.value)}
          />
        </Form.Field>

        <Form.Group widths="equal">
          <Form.Input
            label="Comisión de tarjeta a descontar (%)"
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={form.card_fee_percent}
            onChange={(_, d) => setField("card_fee_percent", d.value)}
          />
          <Form.Input
            label="Retención del negocio (%)"
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={form.retention_percent}
            onChange={(_, d) => setField("retention_percent", d.value)}
          />
        </Form.Group>
        <p style={{ color: "#777", marginTop: -6 }}>
          Comisión 0 = el restaurante la asume y entrega la propina completa. La retención se
          aplica a todo lo repartible y queda en el negocio.
        </p>

        <Button
          primary
          loading={saving}
          disabled={form.model === "PERCENT" && !percentOk}
          onClick={onSave}
        >
          Guardar
        </Button>
      </Form>

      <h3 style={{ marginTop: 30 }}>Propinas de tarjeta por pagar</h3>
      {size(pending) === 0 ? (
        <p style={{ color: "#777" }}>No hay nada pendiente.</p>
      ) : (
        <Table compact unstackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Liquidada</Table.HeaderCell>
              <Table.HeaderCell>Persona</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">Por pagar</Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {map(pending, (share) => (
              <Table.Row key={share.id}>
                <Table.Cell>{moment.parseZone(share.settled_at).format("DD/MM/YYYY HH:mm")}</Table.Cell>
                <Table.Cell>
                  {share.name} · {positionText(share.position)}
                </Table.Cell>
                <Table.Cell textAlign="right">{money(share.card_amount)}</Table.Cell>
                <Table.Cell textAlign="right">
                  <Button
                    basic
                    size="mini"
                    onClick={() => {
                      setPayTarget(share);
                      setPayNote("");
                    }}
                  >
                    <Icon name="check" /> Pagada
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      <ModalConfirm
        title="¿Marcar como pagada?"
        content={
          <div>
            <p>
              {payTarget && `${payTarget.name}: ${money(payTarget.card_amount)} de propina de tarjeta.`}
            </p>
            <Input
              fluid
              placeholder="Nota (ej. transferencia BCP 15/10)"
              maxLength={200}
              value={payNote}
              onChange={(_, d) => setPayNote(d.value)}
            />
          </div>
        }
        show={!!payTarget}
        onCloseText="Cancelar"
        onClose={() => setPayTarget(null)}
        onConfirmText="Marcar pagada"
        onConfirm={onMarkPaid}
      />
    </div>
  );
}

// "Mesero S/ 46.15 · Cocina S/ 23.08 · …" con los puntos que se están editando.
function pointsExample(points) {
  const rows = POSITIONS.map((p) => [p.text, Number(points?.[p.value] ?? 1)]).filter(
    ([, value]) => value > 0
  );
  const total = rows.reduce((sum, [, value]) => sum + value, 0);
  if (!total) return null;
  return rows.map(([text, value]) => `${text} ${money((100 * value) / total)}`).join(" · ");
}
